import { useState, useEffect } from "react";
import { ArrowRight, CheckCircle, Menu, X, Star,
         Instagram, Linkedin, Twitter } from "lucide-react";
import Logo from "/Logo.png";
import "./AboutPage.css";
import "./shared-dark.css";

const navLinks = [
  { label:"About",   href:"/about"   },
  { label:"Agents",  href:"/agents"  },
  { label:"Hotel",   href:"/hotel"   },
  { label:"Contact", href:"/contact" },
];

const brands = [
  "Sam's Story Hotels","Oasis Fine Dining","Serene Wellness","The Grand Resort",
  "Azure Spa & Clinic","Meridian Hotels","Le Blanc Collection","Nile Luxury Suites","Soleil Restaurant",
];

const agents = [
  { tag:"Hotels",       color:"#c9a84c", bg:"rgba(201,168,76,.1)",  border:"rgba(201,168,76,.25)",  name:"EVA",    role:"Hotel Concierge Agent",         rotate:"-3deg", z:3 },
  { tag:"Restaurants",  color:"#10b981", bg:"rgba(16,185,129,.1)",  border:"rgba(16,185,129,.25)",  name:"DINE",   role:"Dining & Reservations Agent",   rotate:"2deg",  z:2 },
  { tag:"Clinics & Spas",color:"#818cf8",bg:"rgba(129,140,248,.1)", border:"rgba(129,140,248,.25)", name:"SERENE", role:"Wellness & Appointment Agent",  rotate:"-1deg", z:1 },
];

const values = [
  { num:"01", title:"Luxury in Simplicity",       desc:"True sophistication lies in removing friction. Every detail designed to feel calm, natural, and effortless." },
  { num:"02", title:"Human-Centered Intelligence", desc:"AI that listens, understands, and remembers — creating genuine connection through intelligent empathy." },
  { num:"03", title:"Innovation with Soul",        desc:"We build not for novelty, but for meaning — intelligence that learns, adapts, and elevates every interaction." },
  { num:"04", title:"Hospitality at the Core",     desc:"Everything we build starts with a deep understanding of hotels, restaurants, and the people who run them." },
];

const testimonials = [
  { quote:"AI Squared completely transformed how our guests book and interact. The WhatsApp concierge feels so natural — guests say it's like texting a real human.", name:"Amira A.", role:"Operations Director", company:"Sam's Story Hotels" },
  { quote:"Our restaurant reservations doubled since integrating AI Squared. It handles messages instantly and remembers guests' preferences perfectly.", name:"Karim E.", role:"General Manager", company:"Oasis Fine Dining" },
  { quote:"AI Squared is the assistant we didn't know we needed — it saved our team hours each day while keeping our clients happier than ever.", name:"Nadia R.", role:"Founder", company:"Serene Wellness Clinics" },
];

export default function AboutPage() {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add("hn-in"); obs.unobserve(e.target); } }),
      { threshold: 0.1 }
    );
    document.querySelectorAll<HTMLElement>(".hn-up").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="hn">

      {/* ═══ NAV ═══ */}
      <nav className="hn-nav">
        <div className="hn-nav-wrap">
          <a href="/" className="hn-logo"><img src={Logo} alt="AI²"/></a>
          <div className="hn-nav-links">{navLinks.map(l => <a key={l.href} href={l.href}>{l.label}</a>)}</div>
          <div className="hn-nav-end">
            <a href="/demo"            className="hn-nav-demo">Book a Demo</a>
            <a href="/dashboard/login" className="hn-nav-login">Staff Login →</a>
            <button className="hn-burger" onClick={() => setMobileOpen(v => !v)}>
              {mobileOpen ? <X size={20}/> : <Menu size={20}/>}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="hn-mob">
            {navLinks.map(l => <a key={l.href} href={l.href} onClick={() => setMobileOpen(false)}>{l.label}</a>)}
            <a href="/demo" onClick={() => setMobileOpen(false)}>Book a Demo</a>
            <a href="/dashboard/login" onClick={() => setMobileOpen(false)} className="hn-mob-login">Staff Login →</a>
          </div>
        )}
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="ab-hero">
        <div className="ab-hero-grid"/>
        <div className="ab-hero-glow-a"/>
        <div className="ab-hero-glow-b"/>
        {/* Large background brand mark */}
        <div className="ab-hero-watermark">AI²</div>

        <div className="ab-hero-inner">
          {/* Left */}
          <div className="ab-hero-left hn-up">
            <div className="hn-badge"><span className="hn-badge-dot"/>Hospitality AI · About Us</div>
            <h1 className="ab-h1">
              <span className="ab-h1-a">The Intelligence</span>
              <span className="ab-h1-b">Behind Luxury.</span>
            </h1>
            <p className="ab-hero-sub">
              AI Squared was built by hospitality insiders who know the real cost of a missed message, a slow response, and a guest who never came back.
            </p>
            <div className="ab-hero-btns">
              <a href="/agents" className="hn-btn">Meet Our Agents <ArrowRight size={14}/></a>
              <a href="/demo"   className="hn-btn-ghost">Book a Demo</a>
            </div>
          </div>

          {/* Right — stacked agent cards */}
          <div className="ab-agents-stack hn-up">
            {agents.map((a, i) => (
              <div key={a.name} className="ab-acard" style={{
                transform:`rotate(${a.rotate})`,
                zIndex: a.z,
                top: `${i * 44}px`,
                borderColor: a.border,
              }}>
                <div className="ab-acard-accent" style={{background:`linear-gradient(90deg,${a.color},transparent)`}}/>
                <div className="ab-acard-top">
                  <span className="ab-acard-tag" style={{color:a.color, background:a.bg, border:`1px solid ${a.border}`}}>{a.tag}</span>
                  <span className="ab-acard-ai" style={{color:a.color, background:a.bg}}>AI²</span>
                </div>
                <div className="ab-acard-name" style={{color: i===0 ? "#f1f5f9" : "rgba(241,245,249,.6)"}}>{a.name}</div>
                <div className="ab-acard-role">{a.role}</div>
                {i === 0 && (
                  <div className="ab-acard-online"><span className="ab-acard-dot"/>Online · Handling guests</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ MARQUEE ═══ */}
      <div className="hn-marquee">
        <div className="hn-mq-track">
          {[...brands, ...brands].map((b, i) => (
            <span key={i} className="hn-mq-item">{b}<span className="hn-mq-dot">·</span></span>
          ))}
        </div>
      </div>

      {/* ═══ STORY ═══ */}
      <section className="ab-story">
        <div className="ab-story-wrap">
          <div className="ab-story-text hn-up">
            <div className="hn-eyebrow" style={{color:"#c9a84c",background:"rgba(201,168,76,.09)",border:"1px solid rgba(201,168,76,.22)"}}>Our Story</div>
            <h2 className="ab-h2">Built by People<br/><span className="ab-gold">Who Know Hospitality.</span></h2>
            <p className="ab-p">
              We didn't build AI Squared in a vacuum. Our founding team spent years inside hotels and restaurants — running front desks, managing reservations, handling VIP guests, and dealing with the chaos of peak season.
            </p>
            <p className="ab-p">
              We knew the pressure of a guest message going unanswered at midnight. We felt the frustration of disconnected systems. So we built the platform we always wished existed — intelligent, human, and effortless.
            </p>
            <ul className="ab-story-list">
              <li><CheckCircle size={13} color="#c9a84c"/><span>Anticipates guest needs before they're expressed</span></li>
              <li><CheckCircle size={13} color="#c9a84c"/><span>Simplifies operations without losing warmth</span></li>
              <li><CheckCircle size={13} color="#c9a84c"/><span>Scales luxury through intelligent, personalized care</span></li>
            </ul>
            <a href="/agents" className="ab-story-link">Meet the agents <ArrowRight size={13}/></a>
          </div>

          <div className="ab-story-right hn-up" style={{transitionDelay:"0.1s"}}>
            {/* Brand identity card */}
            <div className="ab-brand-card">
              <div className="ab-brand-mark">AI²</div>
              <div className="ab-brand-tagline">Luxury Intelligence Platform</div>
              <div className="ab-brand-divider"/>
              <div className="ab-brand-agents">
                {agents.map(a => (
                  <div key={a.name} className="ab-brand-agent">
                    <div className="ab-brand-agent-dot" style={{background:a.color}}/>
                    <span className="ab-brand-agent-name" style={{color:a.color}}>{a.name}</span>
                    <span className="ab-brand-agent-role">{a.role}</span>
                  </div>
                ))}
              </div>
              <div className="ab-brand-stats">
                {[{v:"200+",l:"Brands"},{v:"10+",l:"Integrations"},{v:"50+",l:"Languages"}].map(s=>(
                  <div key={s.l} className="ab-brand-stat">
                    <span>{s.v}</span><span>{s.l}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Floating live badge */}
            <div className="ab-live-badge">
              <span className="ab-live-dot"/><span>3 agents live right now</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ BOLD QUOTE ═══ */}
      <section className="ab-quote">
        <div className="ab-quote-grid"/>
        <div className="ab-quote-glow"/>
        <div className="ab-quote-watermark">AI²</div>
        <div className="ab-quote-wrap hn-up">
          <div className="ab-quote-mark">"</div>
          <p className="ab-quote-text">We don't just automate hospitality.<br/><span>We make it feel more human.</span></p>
          <div className="ab-quote-line"/>
          <div className="ab-quote-meta">
            <div className="ab-quote-av">AI²</div>
            <div>
              <div className="ab-quote-name">The AI Squared Team</div>
              <div className="ab-quote-role">Founders · Builders · Hospitality Veterans</div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ VALUES ═══ */}
      <section className="ab-vals">
        <div className="ab-vals-wrap">
          <div className="hn-sec-hd hn-up">
            <div className="hn-eyebrow">What Drives Us</div>
            <h2 className="hn-h2">Our Core Values.</h2>
          </div>
          <div className="ab-vals-grid">
            {values.map((v, i) => (
              <div key={v.num} className="ab-val hn-up" style={{transitionDelay:`${i*.08}s`}}>
                <div className="ab-val-num">{v.num}</div>
                <div className="ab-val-bar"/>
                <h3 className="ab-val-title">{v.title}</h3>
                <p className="ab-val-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ AGENTS SHOWCASE ═══ */}
      <section className="ab-showcase">
        <div className="ab-showcase-grid"/>
        <div className="ab-showcase-wrap">
          <div className="hn-sec-hd hn-up" style={{alignItems:"center",textAlign:"center"}}>
            <div className="hn-eyebrow" style={{color:"#c9a84c"}}>The AI² Agents</div>
            <h2 className="hn-h2" style={{color:"#f1f5f9",textAlign:"center"}}>Meet the Intelligence<br/><span className="hn-gold">Behind Your Brand.</span></h2>
          </div>
          <div className="ab-showcase-cards">
            {[
              { tag:"Hotels", color:"#c9a84c", bg:"rgba(201,168,76,.08)", border:"rgba(201,168,76,.2)", name:"EVA", role:"Hotel Concierge Agent",
                desc:"Handles bookings, check-in, room service, and post-stay feedback — all via WhatsApp & Instagram.",
                chat:[{g:true,t:"Can I upgrade to a sea-view room?"},{g:false,t:"🌊 Sea View available for +$45/night. Shall I apply it?"}] },
              { tag:"Restaurants", color:"#10b981", bg:"rgba(16,185,129,.08)", border:"rgba(16,185,129,.2)", name:"DINE", role:"Dining & Reservations Agent",
                desc:"Handles reservations, menu questions, and real-time upselling — zero wait time, every time.",
                chat:[{g:true,t:"Table for 2 at 8pm tonight?"},{g:false,t:"✅ Confirmed! Table for 2 at 8:00 PM. 🍽️"}] },
              { tag:"Clinics & Spas", color:"#818cf8", bg:"rgba(129,140,248,.08)", border:"rgba(129,140,248,.2)", name:"SERENE", role:"Wellness & Appointment Agent",
                desc:"Schedules appointments, sends reminders, and manages loyalty — with warmth and discretion.",
                chat:[{g:true,t:"Book a deep tissue massage Saturday?"},{g:false,t:"🌿 11:00 AM Saturday confirmed. See you then!"}] },
            ].map((a, i) => (
              <div key={a.name} className="ab-scard hn-up" style={{transitionDelay:`${i*.1}s`}}>
                <div className="ab-scard-accent" style={{background:`linear-gradient(90deg,${a.color},transparent)`}}/>
                <div className="ab-scard-top">
                  <span className="ab-scard-tag" style={{color:a.color,background:a.bg,border:`1px solid ${a.border}`}}>{a.tag}</span>
                  <span className="ab-scard-ai" style={{color:a.color,background:a.bg}}>AI²</span>
                </div>
                <div className="ab-scard-name">{a.name}</div>
                <div className="ab-scard-role">{a.role}</div>
                <p className="ab-scard-desc">{a.desc}</p>
                <div className="ab-scard-sep"/>
                <div className="ab-scard-chat">
                  {a.chat.map((m,j)=>(
                    <div key={j} className={`ab-scard-bbl${m.g?"":" ab-scard-bbl-ai"}`} style={!m.g?{borderColor:`${a.color}20`}:{}}>{m.t}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="ab-showcase-cta hn-up">
            <a href="/agents" className="hn-btn">See All Agents <ArrowRight size={14}/></a>
          </div>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section className="ab-stats">
        <div className="ab-stats-grid"/>
        <div className="ab-stats-wrap">
          <div className="ab-stats-hd hn-up">
            <div className="hn-eyebrow" style={{color:"rgba(201,168,76,.85)"}}>By the Numbers</div>
            <h2 className="hn-h2" style={{color:"#f1f5f9",marginBottom:0}}>Results That Speak<br/><span className="hn-gold">For Themselves.</span></h2>
            <p style={{color:"rgba(255,255,255,.4)",fontSize:14,lineHeight:1.7,maxWidth:280,margin:0}}>Measured across real deployments at hotels, restaurants, and wellness brands worldwide.</p>
          </div>
          <div className="ab-stats-nums">
            {[
              {v:"< 3s",   l:"Average AI response time",  sub:"Across WhatsApp & Instagram"},
              {v:"98%",    l:"Guest satisfaction rate",    sub:"Measured post-stay"},
              {v:"10+",    l:"PMS integrations",           sub:"Opera, Cloudbeds & more"},
              {v:"7 days", l:"Average time to go live",    sub:"Full onboarding included"},
            ].map((s,i) => (
              <div key={i} className="ab-stat hn-up" style={{transitionDelay:`${i*.08}s`}}>
                <div className="ab-stat-val">{s.v}</div>
                <div className="ab-stat-lbl">{s.l}</div>
                <div className="ab-stat-sub">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="hn-testi">
        <div className="hn-testi-grid-bg"/>
        <div className="hn-testi-wrap">
          <div className="hn-sec-hd hn-up" style={{textAlign:"center",alignItems:"center"}}>
            <div className="hn-eyebrow" style={{color:"#c9a84c"}}>What They Say</div>
            <h2 className="hn-h2" style={{color:"#f1f5f9"}}>Loved by Hospitality<br/><span className="hn-gold">Leaders Worldwide.</span></h2>
          </div>
          <div className="hn-testi-grid">
            {testimonials.map((t,i) => (
              <div key={i} className="hn-tcard hn-up" style={{transitionDelay:`${i*.1}s`}}>
                <div className="hn-tcard-stars">{[...Array(5)].map((_,j)=><Star key={j} size={14} fill="#c9a84c" color="#c9a84c"/>)}</div>
                <p className="hn-tcard-quote">"{t.quote}"</p>
                <div className="hn-tcard-author">
                  <div className="hn-tcard-av">{t.name[0]}</div>
                  <div><div className="hn-tcard-name">{t.name}</div><div className="hn-tcard-role">{t.role} · {t.company}</div></div>
                </div>
              </div>
            ))}
          </div>
          <p className="hn-testi-foot hn-up">Join <strong>200+ hospitality leaders</strong> shaping the future of intelligent luxury.</p>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="hn-cta">
        <div className="hn-cta-ring hn-cta-r1"/><div className="hn-cta-ring hn-cta-r2"/><div className="hn-cta-ring hn-cta-r3"/>
        <div className="hn-cta-glow"/>
        <div className="hn-cta-wrap">
          <div className="hn-eyebrow hn-up" style={{color:"#c9a84c",textAlign:"center"}}>Let's Talk Luxury</div>
          <h2 className="hn-cta-h2 hn-up">Transform Your<br/>Guest Experience.</h2>
          <p className="hn-cta-sub hn-up">Let's automate your brand's next chapter — live in 7 days.</p>
          <div className="hn-cta-btns hn-up">
            <a href="/agents" className="hn-btn hn-btn-cta">Explore Agents <ArrowRight size={14}/></a>
            <a href="/demo"   className="hn-btn-ghost hn-btn-ghost-lt">Book a Demo</a>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="hn-footer">
        <div className="hn-footer-wrap">
          <div className="hn-footer-brand">
            <img src={Logo} alt="AI Squared" className="hn-footer-logo"/>
            <p>Intelligent hospitality experiences for hotels, restaurants, spas, and wellness brands worldwide.</p>
            <div className="hn-footer-socials">
              <a href="#" className="hn-social" aria-label="Instagram"><Instagram size={15}/></a>
              <a href="#" className="hn-social" aria-label="LinkedIn"><Linkedin size={15}/></a>
              <a href="#" className="hn-social" aria-label="Twitter"><Twitter size={15}/></a>
            </div>
          </div>
          {[
            {h:"Product",ls:[["Agents","/agents"],["Hotel Dashboard","/hotel"],["Integrations","/about"],["Pricing","/demo"]]},
            {h:"Company",ls:[["About","/about"],["Contact","/contact"],["Book a Demo","/demo"],["Careers","#"]]},
            {h:"Sectors",ls:[["Hotels & Resorts","/hotel"],["Restaurants","/agents"],["Spas & Wellness","/agents"],["Clinics","/agents"]]},
          ].map(col=>(
            <div key={col.h} className="hn-fcol">
              <div className="hn-fcol-h">{col.h}</div>
              {col.ls.map(([l,h])=><a key={l} href={h}>{l}</a>)}
            </div>
          ))}
        </div>
        <div className="hn-footer-btm">
          <span>© {new Date().getFullYear()} AI Squared. All rights reserved.</span>
          <div><a href="/privacy">Privacy</a><a href="/terms">Terms</a></div>
        </div>
      </footer>

    </div>
  );
}
